/**
 * Inline confirmation card rendered for the `propose_memory_write` Mads tool.
 *
 * When Mads v2 fires `propose_memory_write`, the backend handler parks the
 * tool call (see `core/src/domains/managed-agent/memoryToolHandler.ts`) and
 * the frontend renders this card anchored to the same `tool_use` event.
 * The user clicks Save or Skip → the click POSTs to
 * `/core/managed-agent/memory/confirm` → the parked handler unblocks and
 * returns the `tool_result` to the agent. Once the result arrives, the card
 * flips to a small "Saved." / "Skipped." pill.
 *
 * The card never writes to memory itself — that happens in the backend
 * handler after the decision is recorded.
 */

import React, { useState } from "react";
import { Brain, Check, X, Loader2, CheckCircle2, CircleSlash } from "lucide-react";
import { toast } from "sonner";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";

interface ProposalInput {
  path?: unknown;
  content?: unknown;
  reason?: unknown;
}

interface Props {
  toolUseId: string;
  input?: ProposalInput | unknown;
  /** Tool result text once the backend handler returns. Falsy while parked. */
  resultText: string | null;
  /** Whether the backend returned an error result. */
  isError: boolean;
}

function pickString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function MemoryProposalCard({ toolUseId, input, resultText, isError }: Props) {
  const proposal = (input ?? {}) as ProposalInput;
  const path = pickString(proposal.path);
  const content = pickString(proposal.content);
  const reason = pickString(proposal.reason);

  const [submitting, setSubmitting] = useState<"approve" | "deny" | null>(null);
  const [localDecision, setLocalDecision] = useState<"approve" | "deny" | null>(null);
  const [expanded, setExpanded] = useState(false);

  const resolved = Boolean(resultText) || localDecision !== null;

  const submit = async (decision: "approve" | "deny") => {
    if (submitting || resolved) return;
    setSubmitting(decision);
    try {
      const token = await getAccessToken();
      const res = await fetch(resolveApiUrl("/core/managed-agent/memory/confirm"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ toolUseId, decision }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Memory confirm failed (${res.status})`);
      }
      setLocalDecision(decision);
    } catch (err) {
      toast.error("Couldn't record memory decision", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSubmitting(null);
    }
  };

  // Resolved → small pill.
  if (resolved) {
    const skipped =
      localDecision === "deny" ||
      (resultText && /skipped|user skipped/i.test(resultText));
    return (
      <div className="ds-caption my-1.5 inline-flex items-center gap-2 rounded-full bg-surface-low px-3 py-1.5 text-foreground/70">
        {skipped ? (
          <CircleSlash className="w-3.5 h-3.5" />
        ) : isError ? (
          <X className="w-3.5 h-3.5 text-red-500" />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
        )}
        <span>
          {resultText ?? (localDecision === "approve" ? "Saved." : "Skipped.")}
        </span>
      </div>
    );
  }

  // Pending → full proposal card.
  return (
    <div className="ds-body-sm my-1.5 rounded-2xl bg-surface-low border border-foreground/5 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-foreground/5">
        <Brain className="w-3.5 h-3.5 text-primary" />
        <span className="ds-caption text-foreground/80">Save to memory?</span>
      </div>
      <div className="px-3 py-2 space-y-2">
        {reason && (
          <div className="ds-caption text-foreground/70">
            <span className="font-medium text-foreground/60">Reason:</span> {reason}
          </div>
        )}
        {path && (
          <div className="ds-caption ds-mono text-foreground/70">
            <span className="font-sans font-medium text-foreground/60">Path:</span>{" "}
            {path}
          </div>
        )}
        {content && (
          <div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="ds-caption text-foreground/60 hover:text-foreground/80 underline-offset-2 hover:underline"
            >
              {expanded ? "Hide contents" : "Show contents"}
            </button>
            {expanded && (
              <pre className="ds-micro ds-mono mt-1.5 max-h-60 overflow-auto rounded-lg bg-background/50 p-2 whitespace-pre-wrap text-foreground/80">
                {content}
              </pre>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-foreground/5 bg-background/30">
        <button
          type="button"
          disabled={submitting !== null}
          onClick={() => submit("deny")}
          className="ds-caption inline-flex items-center gap-1 rounded-full px-3 py-1 text-foreground/70 hover:bg-foreground/5 disabled:opacity-50"
        >
          {submitting === "deny" ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <X className="w-3 h-3" />
          )}
          Skip
        </button>
        <button
          type="button"
          disabled={submitting !== null}
          onClick={() => submit("approve")}
          className="ds-caption inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {submitting === "approve" ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          Save
        </button>
      </div>
    </div>
  );
}
